'use client';

import type { PropsWithChildren, RefObject } from 'react';
import {
  Suspense,
  createContext,
  lazy,
  use,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { CartDrawerAlert } from '../molecules/cart-drawer/cart-drawer.constants.ts';

/**
 * The drawer panel is mounted by this provider, which sits in the root layout,
 * so a static import put the entire drawer subtree — cart items, birthday
 * appeal, mini gallery, `cart-sdk` — in the chunk every page downloads, even
 * though the panel starts closed and most visitors never open it.
 *
 * Loading it on first open keeps the panel's code out of that chunk. The cart
 * count in the header comes from `useCart` in `nav-bag.tsx` and is untouched, so
 * the badge still paints immediately.
 */
const CartDrawer = lazy(() =>
  import('../molecules/cart-drawer/cart-drawer.tsx').then((m) => ({
    default: m.CartDrawer,
  })),
);

export type CartDrawerReturnFocusRef = RefObject<HTMLElement | null>;

interface CartDrawerContextValue {
  openCartDrawer: (options?: {
    alert?: CartDrawerAlert;
    returnFocusRef?: CartDrawerReturnFocusRef;
  }) => void;
  closeCartDrawer: () => void;
  clearCartDrawerAlert: () => void;
  /** Warm the drawer chunk ahead of an open, e.g. on hover of the cart button. */
  prefetchCartDrawer: () => void;
}

const CartDrawerContext = createContext<CartDrawerContextValue | null>(null);

const prefetchCartDrawerChunk = () => {
  void import('../molecules/cart-drawer/cart-drawer.tsx');
};

export const CartDrawerProvider = ({ children }: PropsWithChildren) => {
  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState<CartDrawerAlert | null>(null);
  const returnFocusRef = useRef<CartDrawerReturnFocusRef | null>(null);
  // Once the drawer has been opened we keep it mounted, so closing and
  // reopening doesn't suspend again.
  const [hasOpened, setHasOpened] = useState(false);

  const handleOpenChange = useCallback((nextIsOpen: boolean) => {
    if (!nextIsOpen) {
      setAlert(null);
    }
    setOpen(nextIsOpen);
  }, []);

  const value = useMemo(
    () => ({
      openCartDrawer: (options?: {
        alert?: CartDrawerAlert;
        returnFocusRef?: CartDrawerReturnFocusRef;
      }) => {
        returnFocusRef.current = options?.returnFocusRef ?? null;
        setAlert(options?.alert ?? null);
        setHasOpened(true);
        setOpen(true);
      },
      closeCartDrawer: () => handleOpenChange(false),
      clearCartDrawerAlert: () => setAlert(null),
      prefetchCartDrawer: prefetchCartDrawerChunk,
    }),
    [handleOpenChange],
  );

  return (
    <CartDrawerContext value={value}>
      {children}
      {hasOpened && (
        <Suspense fallback={null}>
          <CartDrawer
            open={open}
            onOpenChange={handleOpenChange}
            alert={alert}
            onAlertClose={() => setAlert(null)}
            returnFocusRef={returnFocusRef.current ?? undefined}
          />
        </Suspense>
      )}
    </CartDrawerContext>
  );
};

export const useCartDrawer = () => {
  const context = use(CartDrawerContext);

  if (!context) {
    throw new Error('useCartDrawer must be used within CartDrawerProvider');
  }

  return context;
};
